import { Check, ChevronsUpDown } from "lucide-react";
import { type ComponentPropsWithoutRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "~/components/ui";
import type { ParentCategoryOption } from "~/features/admin/categories/types";
import { cn } from "~/lib/utils";

const ROOT_VALUE = "__root";
const INDENT_PX = 14;

interface ParentCategoryComboboxProps extends Omit<ComponentPropsWithoutRef<typeof Button>, "value" | "onChange"> {
  options: ParentCategoryOption[];
  /** Full path of the selected parent, or `null` for "no parent (tree root)". */
  value: string | null;
  onChange: (parentPath: string | null) => void;
  /** Accessible name of the trigger (the visible label is rendered by the form field). */
  label: string;
  /** Offer "no parent (root)". Only a root category's own edit needs it — for anything else it is a dead end. */
  allowRoot: boolean;
  ariaInvalid?: boolean;
}

/**
 * Searchable parent picker for the category form. Options are indented by
 * depth so the list reads like the hierarchy table; typing filters on both
 * the category name and its path. A path that has no registered category
 * (possible for legacy data) is still shown as the current value.
 */
export function ParentCategoryCombobox({
  options,
  value,
  onChange,
  label,
  allowRoot,
  ariaInvalid,
  ...triggerProps
}: ParentCategoryComboboxProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const selected = value ? (options.find((option) => option.path === value) ?? null) : null;
  const rootLabel = t("feature.categories.form.rootParent");

  const select = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          {...triggerProps}
          type="button"
          variant="outline"
          role="combobox"
          aria-label={label}
          aria-expanded={open}
          aria-invalid={ariaInvalid}
          className="w-full justify-between font-normal"
        >
          <span className="flex min-w-0 items-center gap-2">
            {value === null ? (
              <span className="truncate">{rootLabel}</span>
            ) : selected ? (
              <>
                <span className="truncate">{selected.name}</span>
                <span className="truncate font-mono text-12-regular text-fg-3">{selected.path}</span>
              </>
            ) : (
              <>
                <span className="truncate font-mono">{value}</span>
                <span className="shrink-0 text-12-regular text-fg-3">
                  {t("feature.categories.form.unregisteredParent")}
                </span>
              </>
            )}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={t("feature.categories.form.parentPlaceholder")} />
          <CommandList className="max-h-72">
            <CommandEmpty>{t("ui.notFound")}</CommandEmpty>
            {allowRoot && (
              <>
                <CommandGroup>
                  <CommandItem value={ROOT_VALUE} keywords={[rootLabel]} onSelect={() => select(null)}>
                    <Check className={cn("h-4 w-4", value === null ? "opacity-100" : "opacity-0")} />
                    <span className="text-fg-2">{rootLabel}</span>
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.id}
                  value={option.path}
                  keywords={[option.name]}
                  onSelect={() => select(option.path)}
                >
                  <Check className={cn("h-4 w-4 shrink-0", option.path === value ? "opacity-100" : "opacity-0")} />
                  <span className="flex min-w-0 items-baseline gap-2" style={{ paddingLeft: option.depth * INDENT_PX }}>
                    <span className="truncate">{option.name}</span>
                    <span className="truncate font-mono text-12-regular text-fg-3">{option.path}</span>
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
